import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { logout } from "../../app/auth/authSlice";
import { useDispatch } from "react-redux";

function formatMoney(amount) {
  const n = parseFloat(amount);
  if (Number.isNaN(n)) return "£0.00";
  return `£${n.toFixed(2)}`;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function cardSurface(style, isDarkMode) {
  const key = (style || "Default").toLowerCase();
  if (key === "premium") {
    return {
      background: "linear-gradient(145deg, #4a7dff 0%, #2f5cf0 55%, #1e4ad8 100%)",
      color: "#fff",
      border: "none",
    };
  }
  if (key === "golden") {
    return {
      background: "linear-gradient(145deg, #d4a017 0%, #c48912 50%, #a8730e 100%)",
      color: "#fff",
      border: "none",
    };
  }
  return {
    background: isDarkMode ? "#2d3436" : "#ffffff",
    color: isDarkMode ? "#f1f3f5" : "#212529",
    border: isDarkMode ? "1px solid #404040" : "1px solid #e9ecef",
  };
}

function SmsTopUps() {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [purchasingId, setPurchasingId] = useState(null);
  const [balance, setBalance] = useState(null);
  const [packages, setPackages] = useState([]);

  const handleAuthError = useCallback(
    (err) => {
      if (err?.response?.status === 401) {
        dispatch(logout());
        setTimeout(() => navigate("/user/login", { replace: true }), 800);
        return true;
      }
      return false;
    },
    [dispatch, navigate]
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [balRes, pkgRes] = await Promise.all([
        axiosInstance.get(APIs.EPOS_GET_SMS_BALANCE),
        axiosInstance.get(APIs.EPOS_GET_SMS_PACKAGES),
      ]);

      if (balRes.data?.status) {
        setBalance(balRes.data.data || null);
      } else {
        setBalance(null);
        showErrorToast(balRes.data?.message || "Failed to load SMS balance");
      }

      if (pkgRes.data?.status) {
        setPackages(Array.isArray(pkgRes.data.data) ? pkgRes.data.data : []);
      } else {
        setPackages([]);
        showErrorToast(pkgRes.data?.message || "Failed to load SMS packages");
      }
    } catch (err) {
      console.error(err);
      if (!handleAuthError(err)) {
        showErrorToast(
          err.response?.data?.message || "Failed to load SMS top-up data"
        );
      }
    } finally {
      setLoading(false);
    }
  }, [handleAuthError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePurchase = async (pkg) => {
    const id = pkg.smsPackageID ?? pkg.SmsPackageID ?? pkg.packageID ?? pkg.PackageID;
    if (!id) return;

    try {
      setPurchasingId(id);
      const returnBaseUrl = `${window.location.origin}/messaging/smsTopUps/complete`;
      const response = await axiosInstance.post(APIs.EPOS_PURCHASE_SMS_PACKAGE, {
        SmsPackageID: id,
        PackageID: id,
        ReturnBaseUrl: returnBaseUrl,
      });

      if (!response.data?.status) {
        showErrorToast(
          response.data?.message || "Unable to start SMS package purchase"
        );
        return;
      }

      const checkout = response.data.data?.checkout || response.data.data?.Checkout;
      const checkoutUrl =
        checkout?.checkoutUrl ||
        checkout?.CheckoutUrl ||
        response.data.data?.checkoutUrl;

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }

      showSuccessToast(
        response.data.message ||
          "Invoice created. Pay it from Subscription Invoices if checkout did not open."
      );
      navigate("/subscription-invoices");
    } catch (err) {
      console.error(err);
      if (!handleAuthError(err)) {
        showErrorToast(
          err.response?.data?.message || "Unable to purchase SMS package"
        );
      }
    } finally {
      setPurchasingId(null);
    }
  };

  const smsTotal = balance?.smsTotal ?? balance?.SmsTotal ?? 0;
  const smsUsed = balance?.smsUsed ?? balance?.SmsUsed ?? 0;
  const smsLeft = balance?.smsLeft ?? balance?.SmsLeft ?? 0;
  const lastTopUp = balance?.lastTopUpDate ?? balance?.LastTopUpDate;
  const lastPurchased =
    balance?.lastPurchasedSms ?? balance?.LastPurchasedSms ?? 0;

  const muted = isDarkMode ? "#adb5bd" : "#6c757d";
  const panelBg = isDarkMode ? "#1e2224" : "#ffffff";
  const panelBorder = isDarkMode ? "1px solid #404040" : "1px solid #e9ecef";

  return (
    <div className="content">
      {loading && <Preloader />}
      <div className="container-fluid">
        <div className="d-flex align-items-start mb-3 flex-wrap gap-2">
          <div>
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item active">SMS Top-ups</li>
            </ol>
            <h1 className="page-header mb-0">SMS Top-ups</h1>
            <p className="mb-0 mt-1 small" style={{ color: muted }}>
              Buy SMS credit packs. Credits do not expire by month; finished packs drop off the totals below.
            </p>
          </div>
          <div className="ms-auto">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={fetchData}
              disabled={loading}
            >
              <i className="fa fa-refresh me-1" />
              Refresh
            </button>
          </div>
        </div>

        {/* Balance card */}
        <div
          className="mb-4 p-3"
          style={{
            borderRadius: "12px",
            border: panelBorder,
            background: panelBg,
          }}
        >
          <div className="fw-semibold mb-3">SMS balance</div>
          <div className="row g-3">
            <div className="col-6 col-md-3">
              <div className="small" style={{ color: muted }}>
                Total
              </div>
              <div className="fs-4 fw-semibold">{smsTotal}</div>
            </div>
            <div className="col-6 col-md-3">
              <div className="small" style={{ color: muted }}>
                SMS used
              </div>
              <div className="fs-4 fw-semibold">{smsUsed}</div>
            </div>
            <div className="col-6 col-md-3">
              <div className="small" style={{ color: muted }}>
                SMS left
              </div>
              <div className="fs-4 fw-semibold" style={{ color: themeColor }}>
                {smsLeft}
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="small" style={{ color: muted }}>
                Last top-up
              </div>
              <div className="fw-semibold">{formatDate(lastTopUp)}</div>
              <div className="small" style={{ color: muted }}>
                {lastPurchased > 0 ? `${lastPurchased} SMS purchased` : "No purchase yet"}
              </div>
            </div>
          </div>
        </div>

        <div className="fw-semibold mb-2">Available packages</div>
        {!loading && packages.length === 0 ? (
          <div
            className="p-4 text-center"
            style={{
              borderRadius: "12px",
              border: panelBorder,
              background: panelBg,
              color: muted,
            }}
          >
            No SMS packages are available yet. Ask your administrator to add packs in Super Admin.
          </div>
        ) : (
          <div className="row g-3">
            {packages.map((pkg) => {
              const id = pkg.smsPackageID ?? pkg.SmsPackageID ?? pkg.packageID;
              const name = pkg.name ?? pkg.Name ?? "SMS Pack";
              const description = pkg.description ?? pkg.Description ?? "";
              const qty = pkg.smsQuantity ?? pkg.SmsQuantity ?? 0;
              const price = pkg.price ?? pkg.Price ?? 0;
              const actual = pkg.actualPrice ?? pkg.ActualPrice ?? price;
              const hasDiscount =
                pkg.hasDiscount ?? pkg.HasDiscount ?? actual > price;
              const style = pkg.cardStyle ?? pkg.CardStyle ?? "Default";
              const surface = cardSurface(style, isDarkMode);
              const isLightCard = (style || "Default").toLowerCase() === "default";
              const busy = purchasingId === id;

              return (
                <div className="col-12 col-md-6 col-xl-4" key={id}>
                  <div
                    className="h-100 d-flex flex-column p-3"
                    style={{
                      borderRadius: "14px",
                      boxShadow: isDarkMode
                        ? "none"
                        : "0 4px 16px rgba(0,0,0,0.06)",
                      ...surface,
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <div className="fw-bold fs-5">{name}</div>
                        {description ? (
                          <div
                            className="small"
                            style={{
                              opacity: isLightCard ? 0.75 : 0.9,
                            }}
                          >
                            {description}
                          </div>
                        ) : null}
                      </div>
                      <div className="fw-semibold text-nowrap">{qty} SMS</div>
                    </div>

                    <div className="mt-auto pt-3 d-flex align-items-end justify-content-between gap-2">
                      <div>
                        {hasDiscount ? (
                          <div
                            className="small text-decoration-line-through"
                            style={{ opacity: 0.8 }}
                          >
                            {formatMoney(actual)}
                          </div>
                        ) : null}
                        <div className="fs-4 fw-bold lh-1">{formatMoney(price)}</div>
                      </div>
                      <button
                        type="button"
                        className="btn px-3"
                        disabled={busy || loading}
                        onClick={() => handlePurchase(pkg)}
                        style={
                          isLightCard
                            ? {
                                backgroundColor: themeColor,
                                border: "none",
                                color: "#fff",
                                borderRadius: "8px",
                                fontWeight: 600,
                              }
                            : {
                                background: "#fff",
                                border: "none",
                                color: "#212529",
                                borderRadius: "8px",
                                fontWeight: 600,
                              }
                        }
                      >
                        {busy ? "Starting…" : "Purchase"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default SmsTopUps;
